import { resolveStockerDockIndex, sortStockerDocks, type StockerDockRef } from '../../utils/stockerDocks';
import { resolveColumnKind, type Port } from '../../utils/ports';
import type { OhtTimelineEvent } from '../../types/amhsStream';
import type { LiveStockerColumnKind } from './liveStockerInventory';

function normalizeStreamColumn(value: unknown): LiveStockerColumnKind | undefined {
  const kind = String(value ?? '')
    .trim()
    .toLowerCase();
  if (kind === 'raw' || kind === 'new') return 'raw';
  if (kind === 'processed' || kind === 'finished') return 'processed';
  return undefined;
}

function columnFromTransferEvent(
  ohtTimelines: Record<string, OhtTimelineEvent[]> | undefined,
  eventId: string | undefined
): LiveStockerColumnKind | undefined {
  if (!eventId || !ohtTimelines) return undefined;
  for (const events of Object.values(ohtTimelines)) {
    for (const event of events) {
      if (event.type !== 'transfer_event' || event.eventId !== eventId) continue;
      return normalizeStreamColumn(event.frame.column);
    }
  }
  return undefined;
}

/** Default 4-port SKU: SL-1/SL-2 = raw, SL-3/SL-4 = processed. */
function columnFromDockIndex(
  dockIndex: number,
  dockCount: number
): LiveStockerColumnKind | undefined {
  if (dockCount < 4) return undefined;
  return dockIndex >= 2 ? 'processed' : 'raw';
}

/** Backend port ids like ST-1-LP-2 / ST-1-LP-4 when layout ports do not match. */
function columnFromPortIdSuffix(transferPortId: string | undefined): LiveStockerColumnKind | undefined {
  if (!transferPortId) return undefined;
  const match = /-LP-(\d+)$/i.exec(transferPortId.trim());
  if (!match) return undefined;
  const dock = Number(match[1]);
  if (!Number.isFinite(dock) || dock <= 0) return undefined;
  if (dock <= 2) return 'raw';
  if (dock <= 4) return 'processed';
  return undefined;
}

/**
 * Resolve raw vs processed for a stocker transfer port.
 *
 * Simulation `portId` values often differ from layout port UUIDs (backend ids,
 * SL-3 suffixes, etc.), so an exact id match alone misses processed drops on
 * SL-3 / SL-4. Prefer the streamed column tag, then dock index / port kind.
 */
export function resolveStockerTransferColumnKind(opts: {
  layoutPorts: readonly Port[];
  transferPortId?: string;
  eventId?: string;
  streamColumn?: unknown;
  ohtTimelines?: Record<string, OhtTimelineEvent[]>;
}): LiveStockerColumnKind | undefined {
  const fromStream =
    normalizeStreamColumn(opts.streamColumn) ??
    columnFromTransferEvent(opts.ohtTimelines, opts.eventId);
  if (fromStream) return fromStream;

  const sorted = sortStockerDocks([...opts.layoutPorts] as StockerDockRef[]);
  const dockIndex = resolveStockerDockIndex(sorted, opts.transferPortId);
  const dock = dockIndex != null ? sorted[dockIndex] : undefined;
  if (dock) {
    const explicit = resolveColumnKind(dock as Port, 'stocker');
    if (explicit) return explicit;
    const fromIndex = columnFromDockIndex(dockIndex!, sorted.length);
    if (fromIndex) return fromIndex;
  }

  const direct = opts.layoutPorts.find((port) => port.id === opts.transferPortId);
  if (direct) {
    const explicit = resolveColumnKind(direct, 'stocker');
    if (explicit) return explicit;
  }

  return columnFromPortIdSuffix(opts.transferPortId);
}
